import { useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { bindParticlePointer } from '../lib/particlePointer';
import { polygonPath } from '../lib/canvasGeometry';

const DURATION = 2400;
const clamp = value => Math.max(0, Math.min(1, value));
const hash = value => { const n = Math.sin(value * 127.1 + 311.7) * 43758.5453; return n - Math.floor(n); };

// One deterministic voxel sculpture: plinth, pillar, beam, suspended chains,
// and TWO concave bowls. No external model, image request or WebGL context.
function buildScales() {
  const cells = new Map();
  const put = (x, y, z, part) => {
    x = Math.round(x); y = Math.round(y); z = Math.round(z);
    const key = `${x}:${y}:${z}`;
    if (!cells.has(key)) cells.set(key, { x, y, z, part, seed: hash(x * 13 + y * 7 + z * 19), dx: 0, dy: 0 });
  };
  const block = (x1, x2, y1, y2, depth, part) => {
    for (let y = y1; y <= y2; y++) for (let x = x1; x <= x2; x++) for (let z = 0; z < depth; z++) put(x, y, z, part);
  };
  const line = (x1, y1, x2, y2, part) => {
    const length = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
    for (let i = 0; i <= length; i++) put(x1 + (x2 - x1) * i / length, y1 + (y2 - y1) * i / length, 0, part);
  };
  block(-10, 10, 21, 23, 3, 0);
  block(-7, 7, 19, 20, 2, 0);
  block(-2, 2, 15, 18, 2, 0);
  block(-1, 1, -21, 14, 2, 1);
  for (let y = -24; y <= -20; y++) for (let x = -2; x <= 2; x++) if (Math.abs(x) + Math.abs(y + 22) <= 2) put(x, y, 0, 1);
  block(-21, 21, -16, -15, 2, 2);
  block(-3, 3, -18, -14, 2, 2);
  for (const cx of [-20, 20]) {
    block(cx - 1, cx + 1, -18, -14, 2, 2);
    line(cx, -14, cx - 10, 5, 3);
    line(cx, -14, cx + 10, 5, 3);
    // A shallow open bowl; elliptical rims and curved, tapering voxel shell.
    for (let y = 5; y <= 12; y++) {
      const half = Math.round(10 * Math.sqrt(Math.max(0, 1 - ((y - 5) / 8) ** 2)));
      for (let x = -half; x <= half; x++) {
        if (y === 5 || y >= 10 || Math.abs(x) >= half - 1) put(cx + x, y, 0, 4);
      }
    }
    // Back rim gives the two cups visible depth while leaving the centre open.
    for (let x = -9; x <= 9; x++) put(cx + x, 5 - Math.round(2 * (1 - (x / 10) ** 2)), -1, 4);
  }
  return [...cells.values()].sort((a, b) => a.y - b.y || a.z - b.z || a.x - b.x);
}
const VOXELS = buildScales();

export const CourtAIScalesCanvas = ({ reduced = false, copy, compact = false, active = false }) => {
  const canvasRef = useRef(null), motion = useRef(reduced), replayRef = useRef(null), wakeRef = useRef(null);
  const [supported, setSupported] = useState(true);
  motion.current = reduced;
  useEffect(() => {
    const canvas = canvasRef.current, stage = canvas.parentElement;
    let ctx;
    try { ctx = canvas.getContext('2d'); } catch { /* Static voxel fallback below. */ }
    if (!ctx) { setSupported(false); return; }
    const cells = VOXELS.map(cell => ({ ...cell }));
    const styles = getComputedStyle(stage);
    const color = token => styles.getPropertyValue(token).trim();
    const colors = { ink: color('--product-ink'), accent: color('--product-accent'), top: color('--product-cube-top'), left: color('--product-cube-left'), right: color('--product-cube-right') };
    let width = 0, height = 0, dpr = 1, unit = 1, raf = 0, resizeRaf = 0;
    let visible = false, started = false, disposed = false, elapsed = 0, idle = 0, last = 0, replayCount = 0;
    const pointer = bindParticlePointer(canvas, {
      project: event => { const box = canvas.getBoundingClientRect(); return { x: event.clientX - box.left, y: event.clientY - box.top }; },
      isReduced: () => motion.current,
    });
    let topPath, sidePath;
    const polygon = (path, fill) => { ctx.fillStyle = fill; ctx.fill(path); };
    const cube = (x, y, size, alpha, seed) => {
      ctx.setTransform(dpr, 0, 0, dpr, x * dpr, y * dpr);
      ctx.globalAlpha = alpha;
      polygon(topPath, colors.accent); polygon(topPath, colors.top);
      polygon(sidePath, colors.ink); polygon(sidePath, colors.right);
      ctx.fillStyle = seed > .88 ? colors.accent : colors.ink; ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = colors.left; ctx.fillRect(0, 0, size, size);
    };
    function draw(now, dt = 0) {
      if (!width || !height) return;
      const reduce = motion.current;
      if (reduce) elapsed = DURATION;
      else if (started) { elapsed = Math.min(DURATION, elapsed + dt); idle += dt; }
      const progress = reduce ? 1 : elapsed / DURATION;
      const contact = pointer.read(now);
      const active = contact.active && !reduce;
      const radius = Math.min(width * .30, 120), blend = reduce ? 1 : 1 - Math.exp(-Math.max(dt, 16) * .009);
      let displacement = 0;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, width, height);
      const settle = !reduce && progress > .62 && progress < 1 ? Math.sin((progress - .62) * 22) * .065 * (1 - progress) / .38 : 0;
      const breathe = reduce || progress < 1 ? 0 : Math.sin(idle / 1500) * .14;
      for (const cell of cells) {
        const delay = cell.part * .065 + cell.seed * .12;
        const local = reduce ? 1 : clamp((progress - delay) / (1 - delay));
        const ease = 1 - (1 - local) ** 3;
        let x = cell.x, y = cell.y;
        if (cell.part >= 2) { const yy = y + 16; x = cell.x * Math.cos(settle) - yy * Math.sin(settle); y = cell.x * Math.sin(settle) + yy * Math.cos(settle) - 16; }
        const baseX = width / 2 + (x + cell.z * .35) * unit;
        const baseY = height / 2 + (y - cell.z * .24 + breathe) * unit;
        const angle = cell.seed * Math.PI * 2;
        const cloud = (1 - ease) * Math.min(width, height) * (.12 + cell.seed * .18);
        const vx = baseX - contact.x, vy = baseY - contact.y, distance = active ? Math.hypot(vx, vy) || 1 : 1;
        const force = active ? Math.max(0, 1 - distance / radius) ** 2 * unit * 4 : 0;
        cell.dx += ((reduce ? 0 : vx / distance * force) - cell.dx) * blend;
        cell.dy += ((reduce ? 0 : vy / distance * force) - cell.dy) * blend;
        displacement = Math.max(displacement, Math.hypot(cell.dx, cell.dy));
        const alpha = reduce ? 1 : clamp(.12 + local * 1.6) * (active ? 1 : .94);
        cube(baseX + Math.cos(angle) * cloud + cell.dx, baseY + Math.sin(angle) * cloud + cell.dy, unit * .78, alpha, cell.seed);
      }
      ctx.globalAlpha = 1;
      canvas.dataset.ready = 'true'; canvas.dataset.build = progress.toFixed(3);
      canvas.dataset.state = reduce ? 'static' : !started ? 'waiting' : progress < 1 ? 'assembling' : 'assembled';
      canvas.dataset.displacement = displacement.toFixed(2); canvas.dataset.pointerActive = String(active);
      canvas.dataset.input = contact.input; canvas.dataset.contact = String(contact.contact); canvas.dataset.lastEnd = contact.lastEnd;
      canvas.dataset.replayCount = String(replayCount); canvas.dataset.particleCount = String(cells.length);
    }
    function frame(now) {
      raf = 0;
      if (disposed || !visible || document.hidden) return;
      if (now - last >= 32 || motion.current) {
        const dt = Math.min(64, last ? now - last : 16); last = now; draw(now, dt);
      }
      if (!motion.current) raf = requestAnimationFrame(frame);
    }
    function wake() { if (!disposed && !raf && visible && !document.hidden) { last = 0; raf = requestAnimationFrame(frame); } }
    const resize = () => {
      if (disposed) return;
      width = stage.clientWidth; height = stage.clientHeight; dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      unit = Math.min(width / 76, height / 62);
      const size = unit * .78, dx = size * .35, dy = size * .24;
      topPath = polygonPath([[0, 0], [dx, -dy], [size + dx, -dy], [size, 0]]);
      sidePath = polygonPath([[size, 0], [size + dx, -dy], [size + dx, size - dy], [size, size]]);
      draw(performance.now()); wake();
    };
    const resizeObserver = new ResizeObserver(() => { cancelAnimationFrame(resizeRaf); resizeRaf = requestAnimationFrame(resize); });
    resizeObserver.observe(stage); resize();
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0]; visible = entry.isIntersecting;
      if (entry.intersectionRatio >= .45 && !started) { started = true; elapsed = 0; idle = 0; }
      if (visible) wake();
      else { cancelAnimationFrame(raf); raf = 0; last = 0; started = false; elapsed = 0; pointer.reset('offscreen'); }
    }, { threshold: [0, .45] });
    observer.observe(stage);
    const visibilityChange = () => {
      if (document.hidden) { cancelAnimationFrame(raf); raf = 0; last = 0; }
      else wake();
    };
    document.addEventListener('visibilitychange', visibilityChange);
    replayRef.current = () => {
      if (motion.current) return;
      started = true; elapsed = 0; idle = 0; replayCount++;
      cells.forEach(cell => { cell.dx = 0; cell.dy = 0; });
      pointer.reset('replay'); draw(performance.now()); wake();
    };
    wakeRef.current = () => { draw(performance.now()); wake(); };
    return () => {
      disposed = true; cancelAnimationFrame(raf); cancelAnimationFrame(resizeRaf);
      observer.disconnect(); resizeObserver.disconnect(); pointer.dispose();
      document.removeEventListener('visibilitychange', visibilityChange);
      replayRef.current = null; wakeRef.current = null;
    };
  }, []);
  useEffect(() => { wakeRef.current?.(); }, [reduced]);
  useEffect(() => { if (compact && active && !reduced) replayRef.current?.(); }, [compact, active, reduced]);
  const Frame = compact ? 'span' : 'figure';
  const Stage = compact ? 'span' : 'div';
  return <Frame className="court-ai-scales" data-testid="court-ai-scales-figure">
    <Stage className="court-ai-scales-stage">
      <canvas ref={canvasRef} role="img" aria-label={copy.scalesLabel} data-testid={compact ? 'court-ai-tile-canvas' : 'court-ai-scales-canvas'} hidden={!supported} />
      {!supported && <svg viewBox="-38 -31 76 62" className="court-ai-scales-fallback" role="img" aria-label={copy.scalesLabel} data-testid="court-ai-scales-fallback">{VOXELS.map((cell, i) => <rect key={i} x={cell.x + cell.z * .35} y={cell.y - cell.z * .24} width=".78" height=".78" fill="currentColor" />)}</svg>}
    </Stage>
    {!compact && <figcaption className="court-ai-scales-caption">
      <span data-testid="court-ai-scales-caption">{copy.scalesCaption}</span>
      <Button type="button" variant="ghost" className="court-ai-scales-replay" disabled={reduced || !supported} onClick={() => replayRef.current?.()} data-testid="court-ai-scales-replay-button"><RotateCcw aria-hidden="true" />{reduced || !supported ? copy.static : copy.replay}</Button>
    </figcaption>}
  </Frame>;
};
